import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";


const BASE_URL = process.env.REACT_APP_BASE_URL;
const getToken = () => {
  const storedUser = localStorage.getItem("loggedUser");
  if (!storedUser) return null;
  try {
    const parsed = JSON.parse(storedUser);
    // البحث عن التوكن في أماكن مختلفة
    if (parsed.token) return parsed.token;
    if (parsed.data?.token) return parsed.data.token;
    if (parsed.result?.token) return parsed.result.token;
    if (parsed.user?.token) return parsed.user.token;
    return null;
  } catch {
    return null;
  }
};

// Helper function to get current language
const getCurrentLanguage = () => {
  return i18n.language || 'ar';
};


// Async Thunk for deleting role
export const deleteRole = createAsyncThunk(
  "auth/deleteRole",
  async (roleId, thunkAPI) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("loggedUser"));

      // محاولة الحصول على التوكن من أماكن مختلفة
      let token = storedUser?.token;
      if (!token) {
        token = storedUser?.data?.token;
      }
      if (!token) {
        token = storedUser?.user?.token;
      }

      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.delete(`${BASE_URL}/dashboard/roles/${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "فشل في حذف الصلاحية"
      );
    }
  }
);



export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, { rejectWithValue }) => {
    try {
      const currentLang = getCurrentLanguage();
      // console.log({userData});

      const response = await axios.post(`${BASE_URL}/dashboard/login`, userData, {
        headers: {
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Login failed!";
      return rejectWithValue({ message });
    }
  }
);

// Get user profile
export const getUserProfile = createAsyncThunk(
  "auth/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      try {
        const response = await axios.get(`${BASE_URL}/dashboard/get-profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            "lang": currentLang,
          }
        });
        return response.data;
      } catch (apiError) {
        const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
        if (storedUser) {
          const userData = storedUser.data || storedUser;
          return { status: true, data: userData };
        }
        throw apiError;
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Failed to get profile!";
      return rejectWithValue({ message });
    }
  }
);

// Update user profile
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }
      // console.log({profileData});


      const response = await axios.post(`${BASE_URL}/dashboard/update-profile`, profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update profile!";
      return rejectWithValue({ message });
    }
  }
);

// Change password
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      // ⚠️ ملاحظة: الباك مش عنده endpoint تغيير كلمة سر للأدمن في api.php
      // الـ endpoint الصحيح هو /dashboard/get-profile ثم update-profile
      // لحين إضافته في الباك، الـ request ستصل لـ update-profile
      const response = await axios.post(`${BASE_URL}/dashboard/update-profile`, passwordData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to change password!";
      return rejectWithValue({ message });
    }
  }
);

// Get roles
export const getRoles = createAsyncThunk(
  "auth/getRoles",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${BASE_URL}/dashboard/roles?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get roles!";
      return rejectWithValue({ message });
    }
  }
);

// Get permissions
export const getPermissions = createAsyncThunk(
  "auth/getPermissions",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${BASE_URL}/dashboard/settings/permissions?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get permissions!";
      return rejectWithValue({ message });
    }
  }
);

// Create role
export const createRole = createAsyncThunk(
  "auth/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${BASE_URL}/dashboard/roles`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      // console.log({addRole:response});

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create role!";
      return rejectWithValue({ message });
    }
  }
);

// Update role
export const updateRole = createAsyncThunk(
  "auth/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {

    // console.log({roleId, roleData});

    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${BASE_URL}/dashboard/roles/${roleId}/update`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update role!";
      return rejectWithValue({ message });
    }
  }
);
const storedUser = localStorage.getItem("loggedUser");
const parsedUser = storedUser ? JSON.parse(storedUser) : null;
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: parsedUser,
    profile: null,
    roles: [],
    permissions: [],
    userPermissions: parsedUser?.permissions || [],
    rolesPagination: null,
    permissionsPagination: null,
    meta: null,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
    logout: (state) => {
      state.user = null;
      state.userPermissions = [];
      state.error = null;
      state.success = null;
      localStorage.removeItem("loggedUser");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        const permissions =
          action.payload.data?.permissions || action.payload.permissions || [];

        state.user = action.payload;
        state.userPermissions = permissions;

        // 🔧 FIX: الباك بيرجع token بصيغة "Bearer xxxx"
        // نخزّنه نظيفاً (بدون "Bearer " prefix) عشان الفرونت يضيفه صح
        const rawToken = action.payload.data?.token || "";
        const cleanToken = rawToken.startsWith("Bearer ")
          ? rawToken.replace("Bearer ", "")
          : rawToken;

        const userDataToStore = {
          message: action.payload.message,
          data: {
            ...action.payload.data,
            token: cleanToken, // تخزين التوكن نظيف بدون prefix
          },
          type: action.payload.type,
          code: action.payload.code,
          token: cleanToken, // shortcut مباشر
          permissions: permissions,
        };

        localStorage.setItem("loggedUser", JSON.stringify(userDataToStore));
      })

      // Profile reducers
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;

        // Update state.user and localStorage to ensure Navbar stays in sync
        if (state.user && state.user.data) {
          state.user.data = { ...state.user.data, ...action.payload.data };
        }

        const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
        if (storedUser && storedUser.data) {
          storedUser.data = { ...storedUser.data, ...action.payload.data };
          localStorage.setItem("loggedUser", JSON.stringify(storedUser));
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update profile reducers
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;
        state.success = action.payload.message;

        // Update state.user
        if (state.user && state.user.data) {
          state.user.data = { ...state.user.data, ...action.payload.data };
        }

        // Update localStorage with new user data
        const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
        if (storedUser && storedUser.data) {
          storedUser.data = { ...storedUser.data, ...action.payload.data };
          localStorage.setItem("loggedUser", JSON.stringify(storedUser));
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Change password reducers
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Roles reducers
      .addCase(getRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
        // 🔧 FIX: الباك بيرجع `meta` مش `pagination`
        state.rolesPagination = action.payload.meta;
        state.meta = action.payload.meta;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Permissions reducers
      .addCase(getPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.data;
        state.permissionsPagination = action.payload.pagination;
      })
      .addCase(getPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Create role reducers
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update role reducers
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Delete role reducers
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      });
  },
});

export const { clearState, logout } = authSlice.actions;
export default authSlice.reducer;
